import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { updateUserProfile } from '../store/slices/authSlice';
import { sendProfileUpdateNotification } from '../emailjs/emailService';
import { 
  HiOutlineX, 
  HiOutlineCheck, 
  HiOutlineExclamation,
  HiOutlineGlobe
} from 'react-icons/hi';
import { 
  FaLinkedin, 
  FaTwitter, 
  FaFacebook, 
  FaInstagram 
} from 'react-icons/fa';
import './SocialHandlesModal.scss';

const SocialHandlesModal = ({ isOpen, onClose, userData, onUpdateStart, onUpdateComplete }) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    socialLinkedIn: userData.socialLinkedIn || '',
    socialTwitter: userData.socialTwitter || '',
    socialFacebook: userData.socialFacebook || '',
    socialInstagram: userData.socialInstagram || '',
    socialWeb: userData.socialWeb || '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Add function to handle modal overlay click
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const validateUrl = (url) => {
    if (!url) return true; // Empty URLs are valid
    try {
      const normalizedUrl = /^https?:\/\//i.test(url) ? url : `https://${url}`;
      new URL(normalizedUrl);
      return true;
    } catch {
      return false;
    }
  };

  const normalizeUrl = (url) => {
    const value = String(url || '').trim();
    if (!value) return '';
    return /^https?:\/\//i.test(value) ? value : `https://${value}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    setSuccess('');

    // Notify parent component that update is starting
    if (onUpdateStart) {
      onUpdateStart();
    }

    try {
      // Validate URLs
      const urlFields = ['socialLinkedIn', 'socialTwitter', 'socialFacebook', 'socialInstagram', 'socialWeb'];
      for (const field of urlFields) {
        if (formData[field] && !validateUrl(formData[field])) {
          setError(`Please enter a valid URL for ${field.replace('social', '').toLowerCase()}`);
          setIsSubmitting(false);
          // Notify parent component that update is complete (even on error)
          if (onUpdateComplete) {
            onUpdateComplete();
          }
          return;
        }
      }

      const normalizedFormData = {
        ...formData,
        socialLinkedIn: normalizeUrl(formData.socialLinkedIn),
        socialTwitter: normalizeUrl(formData.socialTwitter),
        socialFacebook: normalizeUrl(formData.socialFacebook),
        socialInstagram: normalizeUrl(formData.socialInstagram),
        socialWeb: normalizeUrl(formData.socialWeb),
      };

      // Check what fields have changed
      const originalData = {
        socialLinkedIn: userData.socialLinkedIn || '',
        socialTwitter: userData.socialTwitter || '',
        socialFacebook: userData.socialFacebook || '',
        socialInstagram: userData.socialInstagram || '',
        socialWeb: userData.socialWeb || '',
      };

      const updatedFields = {};
      Object.keys(originalData).forEach(key => {
        if (normalizedFormData[key] !== originalData[key]) {
          updatedFields[key] = normalizedFormData[key];
        }
      });

      // Only dispatch if there are changes
      if (Object.keys(updatedFields).length > 0) {
        await dispatch(updateUserProfile({
          email: userData.email,
          profileData: updatedFields
        })).unwrap();

        // Send profile update notification email
        try {
          await sendProfileUpdateNotification(
            userData,
            'social_handles',
            updatedFields
          );
          console.log('Social handles update notification sent successfully');
        } catch (emailError) {
          console.error('Failed to send social handles update notification:', emailError);
        }

        setSuccess('Social handles updated successfully!');
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setSuccess('No changes detected');
        setTimeout(() => {
          onClose();
        }, 1500);
      }
    } catch (err) {
      console.error('Error updating social handles:', err);
      setError(err.message || 'Failed to update social handles. Please try again.');
    } finally {
      setIsSubmitting(false);
      // Notify parent component that update is complete
      if (onUpdateComplete) {
        onUpdateComplete();
      }
    }
  };

  const socialFields = [
    {
      name: 'socialLinkedIn',
      label: 'LinkedIn',
      icon: <FaLinkedin />,
      placeholder: 'https://linkedin.com/in/yourprofile',
      color: '#0077b5'
    },
    {
      name: 'socialTwitter',
      label: 'Twitter',
      icon: <FaTwitter />,
      placeholder: 'https://twitter.com/yourhandle',
      color: '#1da1f2'
    },
    {
      name: 'socialFacebook',
      label: 'Facebook',
      icon: <FaFacebook />,
      placeholder: 'https://facebook.com/yourprofile',
      color: '#1877f2'
    },
    {
      name: 'socialInstagram',
      label: 'Instagram',
      icon: <FaInstagram />,
      placeholder: 'https://instagram.com/yourhandle',
      color: '#e4405f'
    },
    {
      name: 'socialWeb',
      label: 'Personal Website',
      icon: <HiOutlineGlobe />,
      placeholder: 'https://yourwebsite.com',
      color: '#6b7280'
    }
  ];

  return (
    <div className="social-handles-modal">
      <div className="modal-overlay" onClick={handleOverlayClick}></div>
      <div className="modal-container">
        <div className="modal-header">
          <h2>Edit Social Handles</h2>
          <button className="close-button" onClick={onClose}>
            <HiOutlineX />
          </button>
        </div>

        {error && (
          <div className="alert error">
            <HiOutlineExclamation />
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div className="alert success">
            <HiOutlineCheck />
            <span>{success}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="social-handles-form">
          <div className="form-description">
            <p>Add your social media profiles and personal website. All fields are optional.</p>
          </div>

          <div className="social-fields">
            {socialFields.map((field) => (
              <div className="form-group" key={field.name}>
                <label htmlFor={field.name} className="social-label">
                  <span className="social-icon" style={{ color: field.color }}>
                    {field.icon}
                  </span>
                  {field.label}
                </label>
                <input
                  type="text"
                  id={field.name}
                  name={field.name}
                  value={formData[field.name]}
                  onChange={handleChange}
                  placeholder={field.placeholder}
                  className="social-input"
                />
              </div>
            ))}
          </div>

          <div className="form-actions">
            <button 
              type="button" 
              className="cancel-button" 
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="save-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SocialHandlesModal;
